/* eslint-disable no-param-reassign -- https://redux-toolkit.js.org/usage/immer-reducers */
import { createSlice, Draft, PayloadAction } from '@reduxjs/toolkit';
import { z } from 'zod';
import { ClaimType } from '~/utils/constants';
import {
  claimAddressSchema,
  claimLineItemSchema,
  shipmentMethodSchema,
  shipmentRateSchema,
} from '~/utils/schemas';
import {
  completeCrewClaim,
  completeDraftClaimLineItem,
  invalidateLineItem,
  setOrder,
} from './shared-actions';

// TODO: would like to separate into return and warranty
export const crewClaimStateSchema = z.nullable(
  z.object({
    orderIdFromPlatform: z.string(),
    claimType: z.nativeEnum(ClaimType),
    claimLineItems: z.array(claimLineItemSchema),
    address: z.nullable(claimAddressSchema),
    toCustomerRate: z.nullable(shipmentRateSchema),
    returnMethod: z.nullable(shipmentMethodSchema),
  }),
);

type CrewClaimState = z.infer<typeof crewClaimStateSchema>;

function assertInitialized(
  state: Draft<CrewClaimState>,
): asserts state is Draft<NonNullable<CrewClaimState>> {
  if (state === null) {
    throw new Error('Crew claim state is not initialized');
  }
}

export const crewClaimSlice = createSlice({
  name: 'crewClaim',
  initialState: null as CrewClaimState,
  reducers: {
    startClaim: (
      state,
      action: PayloadAction<
        Pick<NonNullable<CrewClaimState>, 'claimType' | 'orderIdFromPlatform'>
      >,
    ) => ({
      claimType: action.payload.claimType,
      orderIdFromPlatform: action.payload.orderIdFromPlatform,
      claimLineItems: [],
      address: null,
      toCustomerRate: null,
      returnMethod: null,
    }),
    setAddress: (
      state,
      action: PayloadAction<NonNullable<CrewClaimState>['address']>,
    ) => {
      assertInitialized(state);

      state.address = action.payload;
    },

    upsertClaimLineItem: (
      state,
      action: PayloadAction<
        NonNullable<CrewClaimState>['claimLineItems'][number]
      >,
    ) => {
      assertInitialized(state);

      const index = state.claimLineItems.findIndex(
        (claim) => claim.id === action.payload.id,
      );

      if (index !== -1) {
        state.claimLineItems.splice(index, 1, action.payload);
      } else {
        state.claimLineItems.push(action.payload);
      }
    },

    removeClaimLineItem: (
      state,
      action: PayloadAction<
        NonNullable<CrewClaimState>['claimLineItems'][number]['id']
      >,
    ) => {
      assertInitialized(state);

      const index = state.claimLineItems.findIndex(
        (claim) => claim.id === action.payload,
      );

      if (index > -1) {
        state.claimLineItems.splice(index, 1);
      }
    },

    setReturnMethod: (
      state,
      action: PayloadAction<NonNullable<CrewClaimState>['returnMethod']>,
    ) => {
      assertInitialized(state);

      state.returnMethod = action.payload;
    },

    setToCustomerRate: (
      state,
      action: PayloadAction<NonNullable<CrewClaimState>['toCustomerRate']>,
    ) => {
      assertInitialized(state);

      state.toCustomerRate = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(invalidateLineItem, (state, action) => {
        assertInitialized(state);

        state.claimLineItems = state.claimLineItems.filter(
          (claim) => claim.id !== action.payload,
        );
      })
      .addCase(completeDraftClaimLineItem, (state, action) => {
        assertInitialized(state);

        const index = state.claimLineItems.findIndex(
          (cli) => cli.id === action.payload.id,
        );

        if (index !== -1) {
          state.claimLineItems.splice(index, 1, action.payload);
        } else {
          state.claimLineItems.push(action.payload);
        }
      })
      .addCase(completeCrewClaim, () => null)
      // reset the claim state if the order is different than the one we started with
      // there are cases such as when a line item is invalidated and the order is re-fetched,
      // so we want to reset the claim state
      .addCase(setOrder, (state, action) =>
        state?.orderIdFromPlatform === action.payload.idFromPlatform ?
          state
        : null,
      );
  },
});
